// react
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useGoBack } from 'hooks/useGoBack';
import { ROUTES } from 'router';

// components
import { ActionsCategoryItem } from '@karehero/llama/pwa/pages/ActionsCategoryItem/ActionsCategoryItem';
import { SanityContent } from '@karehero/llama/components/organisms/SanityContent/SanityContent';
import { CSAT } from '@karehero/llama/components/atoms/CSAT/CSAT';

// hooks
import { useActionPlan } from 'hooks/useActionPlan';

// models
import { SanityActionPlan } from '@karehero/models';

// store
import { useDispatch } from 'react-redux';
import { newToast } from 'store/slices/uiSlice';

// tracking
import { TrackingEvent, useCareCircle, useTracking } from 'hooks';

/**
 * ActionPlanContent shows the content library.
 */
export const ActionPlanContent = () => {
  // state
  const [actionPlanItemSanity, setActionPlanItemSanity] = useState<
    Partial<SanityActionPlan>
  >({});
  const [isTracked, setIsTracked] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  // hooks
  const { isIdentified, track } = useTracking();
  const {
    allActionPlanItems: actionPlanItems,
    actionPlanCategories,
    markActionComplete,
    markActionFeedback,
    getActionPlanSanity,
  } = useActionPlan();
  const { actionPlanItemId } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const goBack = useGoBack();
  const { careCircle } = useCareCircle();

  // memos
  const actionPlanItem = useMemo(() => {
    return actionPlanItems?.find((item) => item.id === actionPlanItemId);
  }, [actionPlanItems, actionPlanItemId]);

  const isCSAT = useMemo(() => {
    return actionPlanItem?.isComplete && !actionPlanItem?.isFeedback;
  }, [actionPlanItem]);

  // methods
  const handleFeedback = useCallback(
    (rating: number) => {
      (async () => {
        track(TrackingEvent.ActionPlanItemCSAT, { actionPlanItem, rating });

        if (!actionPlanItem || !careCircle) return;
        const res = await markActionFeedback({
          actionID: actionPlanItem.id,
          careCircleID: careCircle.id,
        });
        if ('error' in res) {
          console.error(res.error);
          return;
        }
        dispatch(
          newToast({
            title: 'Feedback submitted',
            description: 'Thank you for your feedback',
            variant: 'success',
          }),
        );
      })();
    },
    [track, actionPlanItem, markActionFeedback, dispatch, careCircle],
  );

  const handleComplete = useCallback(() => {
    if (!actionPlanItem || actionPlanItem.isComplete) return;
    markActionComplete(actionPlanItem.id);
  }, [actionPlanItem, markActionComplete]);

  // effects
  useEffect(() => {
    (async () => {
      if (!actionPlanItem || !actionPlanItem.sanitySlug) return;
      const res = await getActionPlanSanity(actionPlanItem?.sanitySlug);
      if (!('data' in res) || !res.data) return;
      setActionPlanItemSanity(res.data);
      setIsLoading(false);
    })();
  }, [
    getActionPlanSanity,
    actionPlanItemId,
    actionPlanItem,
    actionPlanItem?.sanitySlug,
  ]);

  useEffect(() => {
    if (!actionPlanItem || !track || isTracked || !isIdentified) return;
    setIsTracked((prev) => {
      if (prev) return prev;
      track(TrackingEvent.ActionPlanItemViewed, { actionPlanItem });
      return true;
    });
  }, [actionPlanItem, track, isTracked, isIdentified]);

  return (
    <ActionsCategoryItem
      title={actionPlanItem?.title || ''}
      description={actionPlanItem?.subTitle || ''}
      imageSrc={actionPlanItemSanity.headerImageUrl}
      contentSlot={<SanityContent content={actionPlanItemSanity.content} />}
      isComplete={actionPlanItem?.isComplete || false}
      checkboxLabel='Mark as complete'
      isCheckboxChecked={actionPlanItem?.isComplete || false}
      onComplete={handleComplete}
      onCheckboxChange={() => handleComplete()}
      onBack={goBack}
      routeButton={
        actionPlanItem?.actionType === 'pathway' && actionPlanItem?.route
          ? {
              label: actionPlanItem.route.includes(ROUTES.BOOK_CALL)
                ? 'Book A Call'
                : 'Continue',
              onClick: () => {
                if (actionPlanItem?.isFirst) {
                  markActionComplete(actionPlanItem.id);
                }
                if (actionPlanItem?.route?.startsWith('http')) {
                  window.open(actionPlanItem.route, '_blank');
                } else {
                  navigate(actionPlanItem.route);
                }
              },
            }
          : undefined
      }
      csatSlot={isCSAT ? <CSAT onPress={handleFeedback} /> : undefined}
      isLoading={isLoading}
    />
  );
};

export default ActionPlanContent;
